// @flow
import React, { Fragment, useContext } from 'react';

import ReactAppContext from 'Context/ReactAppContext';
import * as pleUtils from 'Utils/ple-utils';
import { PLE } from 'Tracking/constants';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { clearAllCacheFront } from 'Utils/graphql-client/cacheFront';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

import { Button } from 'Components/frameworks/button/Button.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';

type Props = {
  hasFacebookConnect: boolean,
  hasQuickLogin: boolean,
  hasWechatConnect: boolean,
  join: {
    ctaLabel: string,
    text: string,
  },
  quickLogin: {
    ctaLabel: string,
    text: string,
    title: string,
    keyPoint: Array<string>,
  },
  signin: {
    ctaLabel: string,
    text: string,
    title: string,
  },
};

export function MemberSignIn({
  hasFacebookConnect,
  hasQuickLogin,
  hasWechatConnect,
  join = {},
  quickLogin = {},
  signin = {},
}: Props) {
  const { locale } = useContext(ReactAppContext);
  return (
    <Fragment>
      <div className="flex flex-col-reverse lg:flex-row" data-testid="signInContent">
        <div className="border-t-1 lg:border-t-0 lg:rtl:border-r-1 border-gray-dark lg:w-1/2 lg:rtl:pr-4 mt-5 pt-5 lg:mt-0 lg:pt-0">
          <div className="flex items-end mb-4">
            <Icon
              className="flex-shrink-0 text-gray-darker"
              isNotMonoChrome
              name={ICONS.ICON_45}
              width="2.5rem"
            />
            <div className="rtl:ml-4">
              <p className="reset-paragraph text-md leading-normal font-brand">{signin.title}</p>
              <p className="m-0 text-sm leading-tight text-gray-darker">{signin.text}</p>
            </div>
          </div>
          <div className="flex">
            <Button
              className="w-full"
              dataTestId="member-nav-signIn"
              isExternalLink
              onClick={() => {
                clearAllCacheFront();
              }}
              trackingData={{
                labelGA: 'memberSpace_signIn',
                categoryGA: 'headerMenu',
                actionGA: 'header_memberSpace',
              }}
              url={pleUtils.buildSigninUrl({ locale })}
            >
              {signin.ctaLabel}
            </Button>
            {hasFacebookConnect ? (
              <Button
                className="rtl:ml-2 rounded-small"
                dataTestId="MemberSignIn-facebook"
                isExternalLink
                minWidth="0"
                onClick={() => {
                  clearAllCacheFront();
                }}
                style={{
                  width: '4rem',
                  backgroundColor: '#4267b2',
                  borderColor: '#4267b2',
                }}
                trackingData={{
                  categoryGA: PLE,
                  actionGA: 'CTA_incentive_FB_connect',
                  labelGA: 'memberSpace',
                }}
                url={pleUtils.buildFacebookConnectUrl({ locale })}
              >
                <Icon name={ICONS.FACEBOOK} />
                <span className="a11y">Club Med - Facebook</span>
              </Button>
            ) : null}
            {hasWechatConnect ? (
              <Button
                className="rtl:ml-2"
                colorBg={COLORS.EMERALD}
                colorBorder={COLORS.EMERALD}
                dataTestId="MemberSignIn-wechat"
                isExternalLink
                minWidth="0"
                style={{ width: '5rem' }}
                trackingData={{
                  categoryGA: PLE,
                  actionGA: 'CTA_incentive_wechat_connect',
                  labelGA: 'memberSpace',
                }}
                url={pleUtils.buildWechatConnectUrl({ locale })}
              >
                <Icon name={ICONS.WECHAT} />
                <span className="a11y">Club Med - Wechat</span>
              </Button>
            ) : null}
          </div>
          <div className="flex lg:justify-center justify-between mt-5">
            <p className="reset-paragraph text-sm leading-tight text-gray-darker">{join.text}</p>
            <TrackClick
              as="a"
              className="reset-button inline-flex items-center ml-3 text-blue font-bold font-sans leading-tight text-sm focus:underline hover:underline"
              data-testid="member-nav-createAccount"
              href={pleUtils.buildSignupUrl({ locale })}
              rel="nofollow"
              trackingData={{
                actionGA: 'header_memberSpace',
                categoryGA: 'headerMenu',
                labelGA: 'memberSpace_createAccount',
              }}
            >
              <span>{join.ctaLabel}</span>
              <Icon className="rtl:ml-1" hasRtl name={ICONS.BOLDER_ARROW_FORTH} width="1em" />
            </TrackClick>
          </div>
        </div>
        {hasQuickLogin && (
          <div className="lg:w-1/2 lg:rtl:pl-4">
            <div className="flex mb-4">
              <Icon
                className="flex-shrink-0 text-gray-darker"
                name={ICONS.DOCUMENT_COLORED}
                width="3.5rem"
                isNotMonoChrome
              />
              <div className="rtl:ml-3">
                <p className="reset-paragraph text-grenadine text-md font-brand">
                  {quickLogin.title}
                </p>
                <p className="m-0 text-sm text-gray-darker leading-tight font-bold">
                  {quickLogin.text}
                </p>
              </div>
            </div>
            <ul className="text-sm leading-tight m-0 p-0 list-none p-0 mb-5">
              {quickLogin.keyPoint &&
                quickLogin.keyPoint.map(key => (
                  <li key={key} className="flex items-start mb-2 rtl:pl-2">
                    <Icon className="flex-shrink-0 text-emerald" name={ICONS.CHECK} width="1rem" />
                    <span className="rtl:pl-2">{key}</span>
                  </li>
                ))}
            </ul>
            <Button
              className="w-full"
              dataTestId="member-nav-quickLogin"
              isExternalLink
              onClick={() => {
                clearAllCacheFront();
              }}
              trackingData={{
                labelGA: 'memberSpace_quickLogin',
                categoryGA: 'headerMenu',
                actionGA: 'header_memberSpace',
              }}
              url={pleUtils.buildSigninUrl({ provider: 'bookingGM', locale })}
            >
              {quickLogin.ctaLabel}
            </Button>
          </div>
        )}
      </div>
    </Fragment>
  );
}
